export class ParsedCssVariable {
  constructor(
    public variable: string,
    public actual: string | undefined,
    public fallback: string | ParsedCssVariable,
    public element: Element,
  ) {}

  get value(): string {
    if (this.actual !== undefined) {
      return this.actual
    }
    if (this.fallback instanceof ParsedCssVariable) {
      return this.fallback.value
    }
    return this.fallback
  }

  get fallbackValue(): string {
    if (this.fallback instanceof ParsedCssVariable) {
      return this.fallback.fallbackValue
    }
    return this.fallback
  }
}


type CssVariableMatch = { groups: { variable: string } }
const cssVariableRegex = /^var\((?<variable>[a-z0-9-]+).*\)$/
const isCssVariableMatch = (match: RegExpMatchArray | null): match is RegExpMatchArray & CssVariableMatch => {
  return match?.groups?.['variable'] !== undefined
}
const matchCssVariable = (cssText: string): string | null => {
  const match = cssText.match(cssVariableRegex)
  return isCssVariableMatch(match) ? match.groups.variable : null
}


type CssFallbackMatch = { groups: { fallback: string } }
const cssFallbackRegex = /^var\([a-z0-9-]+,\s*(?<fallback>.*)\)$/
const isCssFallbackMatch = (match: RegExpMatchArray | null): match is RegExpMatchArray & CssFallbackMatch => {
  return match?.groups?.['fallback'] !== undefined
}
const matchCssFallback = (cssText: string): string | null => {
  const match = cssText.match(cssFallbackRegex)
  return isCssFallbackMatch(match) ? match.groups.fallback : null
}

const fallbackIsVariable = (fallback: string): boolean => {
  return Boolean(matchCssVariable(fallback))
}

export const parseCssVariable = (cssText: string, element: Element = document.body): ParsedCssVariable => {
  const variable = matchCssVariable(cssText)
  if (variable === null) {
    throw new Error(`cssText does not appear to contain a variable: "${cssText}"`)
  }

  const actual = getComputedStyle(element).getPropertyValue(variable) || undefined

  const fallback = matchCssFallback(cssText)
  if (fallback === null) {
    return new ParsedCssVariable(variable, actual, '(no fallback)', element)
  }

  if (fallbackIsVariable(fallback)) {
    const parsedFallback = parseCssVariable(fallback, element)
    return new ParsedCssVariable(variable, actual, parsedFallback, element)
  }

  return new ParsedCssVariable(variable, actual, fallback, element)
}
